/**
 * Public CRM update form — recipients land here from the email link.
 * No auth: the token in the URL IS the auth.
 */
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config/api";

const SERVICES = ["LCL", "FCL", "Air", "Inland"];

export default function CRMRefreshUpdateFormPage() {
  const { token } = useParams();
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [prefillLoaded, setPrefillLoaded] = useState(false);
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    country: "",
    services: [],
    position: "",
    website: "",
    vat_number: "",
    billing_address: "",
    notes: "",
  });

  // Load pre-filled data from the server so the customer only has to
  // confirm/correct rather than fill from scratch.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/crm-refresh/update/${token}/prefill`);
        if (cancelled) return;
        setForm((p) => ({ ...p, ...res.data, notes: p.notes }));
      } catch (e) {
        if (e?.response?.status === 410) {
          setErr("This contact has been unsubscribed and can't be updated through this link.");
        } else if (e?.response?.status === 404) {
          setErr("Link not found or expired.");
        }
      } finally {
        if (!cancelled) setPrefillLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const toggleService = (s) =>
    setForm((p) => ({
      ...p,
      services: p.services.includes(s) ? p.services.filter((x) => x !== s) : [...p.services, s],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.company_name || !form.contact_name || !form.email || !form.phone || !form.country) {
      setErr("Please fill all required fields.");
      return;
    }
    if (form.services.length === 0) {
      setErr("Please select at least one service.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API_URL}/crm-refresh/update/${token}`, form);
      setSubmitted(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ maxWidth: 560, margin: "60px auto", padding: 32, fontFamily: "system-ui", textAlign: "center" }}>
        <h2 style={{ color: "#16a34a" }}>Thanks!</h2>
        <p>Your details have been received. We'll update our CRM and you won't be contacted again this quarter.</p>
        <p style={{ color: "#999", fontSize: 12, marginTop: 32 }}>Europacific Logistics · Koper, Slovenia</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 640, margin: "40px auto", padding: 32, fontFamily: "system-ui" }}>
      <h2>Confirm your contact details</h2>
      <p style={{ color: "#555" }}>
        We've pre-filled what we have on file. Please correct anything wrong
        or fill in what's missing — fields marked <span style={{ color: "#dc2626" }}>*</span> are required.
      </p>
      {!prefillLoaded && !err && (
        <p style={{ color: "#999", fontSize: 13 }}>Loading your details…</p>
      )}
      <form onSubmit={submit} style={{ display: "grid", gap: 14, marginTop: 24 }}>
        <Field label="Company name *" v={form.company_name} onChange={set("company_name")} testid="crm-update-company" />
        <Field label="Full name *" v={form.contact_name} onChange={set("contact_name")} testid="crm-update-name" />
        <Field label="Email *" v={form.email} onChange={set("email")} type="email" testid="crm-update-email" />
        <Field label="Phone *" v={form.phone} onChange={set("phone")} testid="crm-update-phone" />
        <Field label="Country *" v={form.country} onChange={set("country")} testid="crm-update-country" />

        <div>
          <div style={{ fontSize: 13, marginBottom: 6 }}>Services of interest *</div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {SERVICES.map((s) => (
              <label key={s} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input
                  type="checkbox"
                  checked={form.services.includes(s)}
                  onChange={() => toggleService(s)}
                  data-testid={`crm-update-service-${s.toLowerCase()}`}
                />
                {s}
              </label>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 8, paddingTop: 16, borderTop: "1px solid #eee", color: "#666", fontSize: 13 }}>
          Optional but appreciated:
        </div>
        <Field label="Position / title" v={form.position} onChange={set("position")} testid="crm-update-position" />
        <Field label="Website" v={form.website} onChange={set("website")} testid="crm-update-website" />
        <Field label="VAT / company registration" v={form.vat_number} onChange={set("vat_number")} testid="crm-update-vat" />
        <Field label="Billing address" v={form.billing_address} onChange={set("billing_address")} testid="crm-update-billing" />
        <TextArea label="Notes" v={form.notes} onChange={set("notes")} testid="crm-update-notes" />

        {err && <div style={{ color: "#dc2626", fontSize: 13 }}>{err}</div>}

        <button
          type="submit"
          disabled={busy}
          style={{
            background: "#2563eb",
            color: "white",
            border: 0,
            padding: "12px 24px",
            borderRadius: 8,
            fontSize: 16,
            cursor: busy ? "wait" : "pointer",
            marginTop: 8,
          }}
          data-testid="crm-update-submit"
        >
          {busy ? "Submitting…" : "Submit"}
        </button>
      </form>
    </div>
  );
}

function Field({ label, v, onChange, type = "text", testid }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 13, marginBottom: 4 }}>{label}</div>
      <input
        type={type}
        value={v}
        onChange={onChange}
        data-testid={testid}
        style={{
          width: "100%",
          padding: "10px 12px",
          border: "1px solid #d1d5db",
          borderRadius: 6,
          fontSize: 14,
        }}
      />
    </label>
  );
}

function TextArea({ label, v, onChange, testid }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 13, marginBottom: 4 }}>{label}</div>
      <textarea
        value={v}
        onChange={onChange}
        data-testid={testid}
        rows={3}
        style={{
          width: "100%",
          padding: "10px 12px",
          border: "1px solid #d1d5db",
          borderRadius: 6,
          fontSize: 14,
          resize: "vertical",
        }}
      />
    </label>
  );
}
